import { useState, useEffect, useRef } from 'react';

import { loadCMPSDK } from '../adapters/saddlebag';
import { COMPONENT_NAME } from '../constants';
import { OPERATIONAL_EVENTS } from '../types/events';

import type { SDKStatus } from '../types/state';
import type { Logger } from '@web-platform/global-components-types/utils';

export interface UseCMPSDKOptions {
  /**
   * Logger for operational events.
   */
  logger: Logger;

  /**
   * Timeout in ms before SDK load is considered failed.
   * If not provided, the adapter default (10000ms) applies.
   */
  timeout?: number;
}

export interface UseCMPSDKResult {
  /**
   * Current SDK status.
   */
  status: SDKStatus;

  /**
   * Whether the consent banner should be shown.
   * Driven by Ketch SDK callbacks (onShowConsentExperience/onHideConsentExperience).
   *
   * This is the authoritative source for banner visibility - no observer-based
   * approach is used. Ketch SDK directly controls this via callbacks.
   */
  shouldShow: boolean;

  /**
   * Whether fallback consent is active.
   */
  isFallbackActive: boolean;

  /**
   * Error details if SDK failed to load.
   */
  error: { code: string; message: string } | null;
}

/**
 * Hook for managing CMP SDK lifecycle and banner visibility.
 *
 * Banner Visibility Control:
 * This hook uses a callback-based approach (NOT observer-based) to control banner visibility.
 * The Ketch SDK from saddlebag-tracking-preferences provides two key callbacks:
 *
 * - onShowConsentExperience: Called when Ketch determines the consent banner should be shown.
 *   Triggered by Ketch's 'willShowExperience' event when experienceType === 'consent'.
 *   Sets shouldShow to true.
 *
 * - onHideConsentExperience: Called when Ketch determines the consent banner should be hidden.
 *   Triggered by Ketch's 'hideExperience' event when experienceType === 'consent'.
 *   Sets shouldShow to false.
 *
 * IMPORTANT: These callbacks only fire for the consent banner, NOT the preference center.
 * The preference center has its own lifecycle managed separately by Ketch.
 *
 * Timing Guarantee:
 * ConsentBanner calls loadCMPSDK on mount, which registers callbacks before Ketch
 * fires any events. This ensures no race conditions or missed events.
 *
 * @example
 * const { shouldShow, status } = useCMPSDK({ logger });
 *
 * // shouldShow becomes true when Ketch calls onShowConsentExperience
 * // shouldShow becomes false when Ketch calls onHideConsentExperience
 * // (e.g., after user accepts/rejects consent)
 */
export function useCMPSDK(options: UseCMPSDKOptions): UseCMPSDKResult {
  const { logger, timeout } = options;

  const [status, setStatus] = useState<SDKStatus>('idle');
  const [shouldShow, setShouldShow] = useState(false);
  const [isFallbackActive, setIsFallbackActive] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const hasInitialized = useRef(false);
  const mountedRef = useRef(false);
  const loggerRef = useRef(logger);
  const timeoutRef = useRef(timeout);

  useEffect(() => {
    loggerRef.current = logger;
  }, [logger]);

  useEffect(() => {
    timeoutRef.current = timeout;
  }, [timeout]);

  useEffect(() => {
    mountedRef.current = true;

    // Prevent double initialization in StrictMode
    if (hasInitialized.current) {
      return () => {
        mountedRef.current = false;
      };
    }
    hasInitialized.current = true;

    // One-time state transition at mount — cannot loop, StrictMode guard above.

    setStatus('loading');

    loggerRef.current.logOperationalEvent({
      eventName: OPERATIONAL_EVENTS.SDK_LOADING,
      component: COMPONENT_NAME,
    });

    loadCMPSDK({
      onShowConsentExperience: () => {
        if (mountedRef.current) {
          setShouldShow(true);
          loggerRef.current.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.BANNER_SHOW_REQUESTED,
            component: COMPONENT_NAME,
          });
        }
      },

      onHideConsentExperience: () => {
        if (mountedRef.current) {
          setShouldShow(false);
          loggerRef.current.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.BANNER_HIDE_REQUESTED,
            component: COMPONENT_NAME,
          });
        }
      },

      onReady: () => {
        if (mountedRef.current) {
          setStatus('ready');
          loggerRef.current.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.SDK_LOADED,
            component: COMPONENT_NAME,
          });
        }
      },

      onError: (err) => {
        if (mountedRef.current) {
          setStatus('failed');
          setError({
            code: 'SDK_LOAD_FAILED',
            message: err.message,
          });
          setIsFallbackActive(true);
          setShouldShow(false); // Hide banner on failure, fallback consent applied

          loggerRef.current.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.SDK_LOAD_FAILED,
            component: COMPONENT_NAME,
          });

          loggerRef.current.logWarn({
            message: `CMP SDK failed to load: ${err.message}`,
            component: COMPONENT_NAME,
          });

          loggerRef.current.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.FALLBACK_APPLIED,
            component: COMPONENT_NAME,
          });
        }
      },

      timeout: timeoutRef.current,
    });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    status,
    shouldShow,
    isFallbackActive,
    error,
  };
}
